import { Pin, X, MoreHorizontal, Check, ArrowRight, FileText } from "lucide-react";
import MindrailLogo from "./MindrailLogo";

/**
 * "Park a Thought" — 8-second seamless loop.
 *
 *   0.0–1.5s   DEEP WORK     document focused, cursor blinking
 *   1.5–2.5s   MODAL OPENS   PARK A THOUGHT modal fades in, doc dims
 *   2.5–4.0s   TYPING        thought types into input, connector arc draws
 *   4.0–5.5s   SAVED         new card slides into side panel, "Thought parked"
 *   5.5–7.0s   RETURN        modal fades, focus back on document
 *   7.0–8.0s   RESET         seamlessly back to deep work
 *
 * Pure DOM + SVG + CSS keyframes scoped under .mr-park.
 */
export default function ParkThoughtsAnimation() {
  const THOUGHT = "Turn this client process into a template pack later";

  return (
    <div className="absolute inset-0 mr-park overflow-hidden">
      {/* ===== BROWSER WINDOW ===== */}
      <div className="absolute inset-2 bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_24px_60px_rgba(15,23,42,0.10)] overflow-hidden flex flex-col">
        {/* chrome — tab + url */}
        <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-2 py-1.5 flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F9735B]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
          <div className="ml-1.5 flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-t-md rounded-b-none px-1.5 py-1 -mb-[5px] text-[7px]">
            <FileText className="w-2 h-2 text-[#2563EB]" strokeWidth={2.2} />
            <span className="font-display font-semibold text-[#0F172A]">
              Studio North Rebrand
            </span>
            <X className="w-2 h-2 text-[#94A3B8] ml-0.5" strokeWidth={2} />
          </div>
          <div className="ml-auto flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-md px-1.5 py-0.5 text-[7px] text-[#475569] font-label">
            docs.google.com/document/studio-north
          </div>
        </div>

        {/* main: document + side panel */}
        <div className="flex-1 flex min-h-0">
          {/* ===== DOCUMENT (left + center) ===== */}
          <div className="mr-pk-doc flex-1 min-w-0 relative overflow-hidden">
            {/* doc title + menu bar */}
            <div className="px-3 pt-2.5 pb-1.5 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-1.5">
                <span className="w-3.5 h-4 rounded bg-[#2563EB] flex items-center justify-center">
                  <FileText className="w-2 h-2 text-white" strokeWidth={2.4} />
                </span>
                <span className="font-display text-[10px] font-bold text-[#0F172A] truncate">
                  Homepage Copy Draft — Studio North Rebrand
                </span>
              </div>
              <div className="mt-1 flex gap-2 text-[7px] text-[#475569]">
                {["File", "Edit", "View", "Insert", "Format", "Tools", "Help"].map(
                  (m) => (
                    <span key={m}>{m}</span>
                  )
                )}
              </div>
            </div>

            {/* document body */}
            <div className="px-4 pt-3 pb-3 relative">
              <p className="font-display text-[13px] font-bold text-[#0F172A] leading-tight">
                Studio North.
                <br />
                Crafted for Clarity.
              </p>
              <p className="text-[8px] text-[#475569] mt-1.5 leading-snug">
                We create brands and digital experiences that cut through noise
                and connect with the right people.
              </p>

              {/* Section 01 */}
              <div className="mt-3">
                <p className="font-display text-[10px] font-bold text-[#0F172A] flex items-baseline gap-1.5">
                  <span className="text-[#7C3AED] font-mono text-[8px]">01</span>
                  The Challenge
                </p>
                <p className="text-[7px] text-[#475569] mt-1 leading-snug">
                  Studio North needed a refresh that reflects their elevated
                  work and earns the trust of bigger clients.
                </p>
              </div>

              {/* Section 02 */}
              <div className="mt-3">
                <p className="font-display text-[10px] font-bold text-[#0F172A] flex items-baseline gap-1.5">
                  <span className="text-[#7C3AED] font-mono text-[8px]">02</span>
                  Our Approach
                </p>
                <ul className="mt-1.5 space-y-1 text-[7px] text-[#0F172A]">
                  {[
                    "Sharp positioning and messaging",
                    "Bold, minimal visual direction",
                    "Conversion-focused structure",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#EDE9FE] flex items-center justify-center shrink-0">
                        <Check
                          className="w-1.5 h-1.5 text-[#7C3AED]"
                          strokeWidth={3}
                        />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Section 03 */}
              <div className="mt-3">
                <p className="font-display text-[10px] font-bold text-[#0F172A] flex items-baseline gap-1.5">
                  <span className="text-[#7C3AED] font-mono text-[8px]">03</span>
                  What&rsquo;s Next
                </p>
                <p className="text-[7px] text-[#475569] mt-1 leading-snug">
                  Build the new site, refine the content, and launch with
                  confidence.
                </p>
                {/* cursor */}
                <span className="mr-pk-cursor inline-block w-px h-2 bg-[#2563EB] align-middle ml-px" />
              </div>
            </div>

            {/* dim overlay during modal */}
            <div
              className="mr-pk-dim absolute inset-0 bg-[#0F172A]/15 pointer-events-none"
              aria-hidden
            />
          </div>

          {/* ===== MINDRAIL SIDE PANEL ===== */}
          <div className="w-[34%] shrink-0 bg-[#F8FAFC] border-l border-[#E2E8F0] flex flex-col">
            <div className="px-2.5 py-2 border-b border-[#E2E8F0] flex items-center justify-between">
              <MindrailLogo size={26} />
              <div className="flex gap-1 text-[#94A3B8]">
                <Pin className="w-2.5 h-2.5" strokeWidth={2} />
                <X className="w-2.5 h-2.5" strokeWidth={2} />
              </div>
            </div>
            {/* tabs */}
            <div className="px-2 pt-1.5 flex gap-1">
              <span className="px-2 py-0.5 rounded-md bg-[#DBEAFE] text-[#2563EB] text-[8px] font-display font-bold">
                History
              </span>
              <span className="px-2 py-0.5 text-[#475569] text-[8px] font-display font-semibold">
                Insights
              </span>
            </div>
            {/* parked thoughts heading */}
            <div className="px-2.5 pt-2.5 pb-1 flex items-baseline justify-between">
              <p className="font-display text-[10px] font-bold text-[#0F172A]">
                Parked thoughts
              </p>
              <span className="mr-pk-count font-label text-[9px] font-bold text-[#475569]">
                26
              </span>
            </div>

            {/* list */}
            <div className="flex-1 overflow-hidden px-2 space-y-1.5">
              {/* NEW CARD — slides in 50%+ */}
              <div className="mr-pk-newcard relative bg-white border border-[#7C3AED]/25 rounded-lg p-2 shadow-[0_6px_18px_rgba(124,58,237,0.10)]">
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                  <span className="font-label text-[8px] font-bold text-[#7C3AED]">
                    Just now
                  </span>
                </div>
                <p className="font-display text-[9px] font-bold text-[#0F172A] mt-1 leading-snug">
                  Turn this client process into a template pack later
                </p>
                <div className="mt-1.5 flex items-center gap-1 text-[7px] text-[#475569]">
                  <FileText
                    className="w-2 h-2 text-[#2563EB]"
                    strokeWidth={2.2}
                  />
                  docs.google.com
                </div>
                <p className="text-[7px] text-[#475569] mt-0.5 leading-tight truncate">
                  Homepage Copy Draft — Studio North Rebrand
                </p>
                <p className="text-[7px] font-medium text-[#7C3AED] mt-0.5">
                  Linked to current page
                </p>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-[7px] font-semibold px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#2563EB]">
                    Convert to task
                  </span>
                  <MoreHorizontal
                    className="w-2.5 h-2.5 text-[#94A3B8]"
                    strokeWidth={2}
                  />
                </div>
              </div>

              {/* divider */}
              <p className="font-label text-[7px] font-bold text-[#94A3B8] tracking-wider pt-1">
                EARLIER
              </p>

              {[
                {
                  title: "Make a case study from this onboarding flow",
                  src: "framer.com",
                },
                {
                  title: "Research pricing page patterns for SaaS",
                  src: "stripe.com",
                },
              ].map((c) => (
                <div
                  key={c.title}
                  className="bg-white border border-[#E2E8F0] rounded-lg p-1.5"
                >
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]/50" />
                    <span className="font-display text-[8px] font-bold text-[#0F172A] leading-tight line-clamp-2">
                      {c.title}
                    </span>
                  </div>
                  <p className="text-[7px] text-[#475569] mt-0.5">{c.src}</p>
                </div>
              ))}
            </div>

            <div className="px-2 pb-2 pt-1.5">
              <div className="bg-[#EDE9FE] rounded-md px-2 py-1 flex items-center justify-between text-[#7C3AED]">
                <span className="font-display text-[8px] font-bold">
                  View all parked thoughts
                </span>
                <ArrowRight className="w-2.5 h-2.5" strokeWidth={2.4} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== PARK A THOUGHT MODAL ===== */}
      <div
        className="mr-pk-modal absolute"
        style={{
          left: "16%",
          right: "42%",
          top: "32%",
        }}
        aria-hidden
      >
        <div className="bg-white rounded-xl shadow-[0_24px_60px_rgba(15,23,42,0.20)] border border-[#E2E8F0] p-3">
          <p className="font-label text-[8px] tracking-[0.22em] font-bold text-[#7C3AED]">
            PARK A THOUGHT
          </p>
          <div className="mr-pk-input mt-2 bg-[#F8FAFC] border border-[#7C3AED]/35 rounded-md px-2 py-1.5 h-7 flex items-center text-[8px] text-[#0F172A] font-display font-medium overflow-hidden">
            <span className="mr-pk-typed truncate">
              Turn this client process into a template pack later
            </span>
            <span className="mr-pk-caret inline-block w-px h-2.5 bg-[#7C3AED] ml-0.5" />
          </div>
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <span className="text-[8px] font-medium px-2 py-1 rounded border border-[#E2E8F0] text-[#475569]">
              Cancel
            </span>
            <span className="mr-pk-btn text-[8px] font-bold px-2.5 py-1 rounded bg-[#2563EB] text-white">
              Park It
            </span>
          </div>
        </div>
      </div>

      {/* ===== CONNECTOR ARC: modal → side panel ===== */}
      <svg
        viewBox="0 0 400 500"
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
      >
        <defs>
          <marker
            id="mr-pk-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="3"
            refY="3"
            orient="auto"
          >
            <path d="M 0 0 L 6 3 L 0 6 Z" fill="#7C3AED" />
          </marker>
        </defs>
        <path
          className="mr-pk-arc"
          d="M 220 230 Q 280 200 320 230"
          stroke="#7C3AED"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
          markerEnd="url(#mr-pk-arrow)"
        />
      </svg>

      {/* ===== THOUGHT PARKED SUCCESS PILL ===== */}
      <div
        className="mr-pk-success absolute left-1/2 top-[58%] -translate-x-1/2 inline-flex items-center gap-1.5 bg-white border border-[#22C55E]/35 rounded-full px-3 py-1.5 shadow-[0_10px_28px_rgba(34,197,94,0.22)]"
        aria-hidden
      >
        <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#22C55E]">
          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
        </span>
        <span className="font-display text-[10px] font-bold text-[#16A34A] whitespace-nowrap">
          Thought parked
        </span>
      </div>
    </div>
  );
}
